// src/components/Layout/CRMLayout.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Typography } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  CalendarOutlined,
  TeamOutlined,
  EuroOutlined,
  ContactsOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  AreaChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './CRMLayout.css';

const { Sider, Content } = Layout;
const { Text } = Typography;

export interface CRMLayoutProps {
  children: React.ReactNode;
  /**
   * Quando true, rimuove padding/margini interni del Content
   * per permettere alla pagina Impostazioni di "andare a filo"
   * con la top bar e lateralmente (utile con sidebar interna alla pagina).
   */
  isSettings?: boolean;
  /**
   * Quando true, forza il collapse della sidebar
   * (usato quando si apre la pagina Settings per fare spazio al menu laterale)
   */
  forceCollapsed?: boolean;
}

/** Alias ruolo come in Team (UI): USER→STAFF, PROJECT_MANAGER→PJM, ecc. */
const roleAlias = (role?: string) => {
  const r = String(role || '').toUpperCase().replace(/^ROLE_/, '');
  switch (r) {
    case 'ADMIN': return 'ADMIN';
    case 'MANAGER':
    case 'PROJECT MANAGER':
    case 'PROJECT_MANAGER':
    case 'PJM': return 'PJM';
    case 'USER': return 'STAFF';
    case 'CUSTOMER': return 'CUSTOMER';
    case 'FINANCE': return 'FINANCE';
    case 'VIDEOMAKER': return 'VIDEOMAKER';
    default: return role || '—';
  }
};

const CRMLayout: React.FC<CRMLayoutProps> = ({ 
  children, 
  isSettings = false, 
  forceCollapsed = false 
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Effetto per gestire il collapse forzato quando si entra in Settings
  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    }
  }, [forceCollapsed]);

  // Normalizza il ruolo per i permessi (es. ROLE_ADMIN -> ADMIN)
  const normalizedRole = useMemo(() => {
    const r = String(user?.role || '').toUpperCase();
    return r.startsWith('ROLE_') ? r.slice(5) : r;
  }, [user?.role]);

  const canSeeBilling = normalizedRole === 'ADMIN' || normalizedRole === 'FINANCE';
  const canSeeSettings = normalizedRole === 'ADMIN';
  const canSeeTeam = normalizedRole === 'ADMIN';
  const canSeeNews = normalizedRole === 'ADMIN';
  const canSeeLandingPages = normalizedRole === 'ADMIN';

  const menuItems = useMemo(() => {
    const items = [
      { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
      { key: '/customers', icon: <UserOutlined />, label: 'Clienti' },
      { key: '/contacts', icon: <ContactsOutlined />, label: 'Contatti' },
      { key: '/calendar', icon: <CalendarOutlined />, label: 'Calendario' },
      { key: '/consulting', icon: <AreaChartOutlined />, label: 'Consulenze' },
      ...(canSeeLandingPages ? [{ key: '/landing-pages', icon: <AppstoreOutlined />, label: 'Landing Pages' }] : []),
      ...(canSeeNews ? [{ key: '/newsmanager', icon: <FileTextOutlined />, label: 'News' }] : []),
      ...(canSeeTeam ? [{ key: '/team', icon: <TeamOutlined />, label: 'Team' }] : []),
      ...(canSeeBilling ? [{ key: '/billing', icon: <EuroOutlined />, label: 'Pagamenti' }] : []),
      ...(canSeeSettings ? [{ key: '/settings', icon: <SettingOutlined />, label: 'Impostazioni' }] : []),
    ];
    return items;
  }, [canSeeBilling, canSeeSettings, canSeeTeam, canSeeNews, canSeeLandingPages]);

  // Determina l'item selezionato in base al path corrente
  const selectedKey = useMemo(() => {
    const currentPath = location.pathname;

    if (currentPath === '/') return '/';

    const pathSegments = currentPath.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      const baseRoute = `/${pathSegments[0]}`;
      const menuItem = (menuItems as any[]).find(item => item.key === baseRoute);
      if (menuItem) return baseRoute;
    }
    return currentPath;
  }, [location.pathname, menuItems]);

  const handleMenuClick = (e: any) => navigate(e.key);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const userMenu = [
    { key: 'profile', icon: <UserSwitchOutlined />, label: 'Profilo', onClick: () => navigate('/profile') },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: handleLogout, danger: true },
  ];

  const getUserInitials = () => (user?.firstName && user?.lastName)
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
    : 'U';

  return (
    <Layout className="crm-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={280}
        collapsedWidth={80}
        className="crm-sidebar"
        style={{ flex: '0 0 auto' }}
      >
        <div className="crm-sidebar-wrapper">
          <div className="crm-logo-section">
            <div className="crm-logo">
              {!collapsed ? (
                <img
                  src="/logo_miglioriamo_white.png"
                  alt="Logo"
                  style={{ maxWidth: '100%', height: '120px' }}
                />
              ) : null}
            </div>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="crm-collapse-btn"
            />
          </div>

          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={handleMenuClick}
            className="crm-menu"
          />

          <div className="crm-bottom-section">
            <Dropdown menu={{ items: userMenu }} placement="topRight" trigger={['click']}>
              <div className="crm-profile-card">
                <Avatar size={36} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', fontWeight: 600 }}>
                  {getUserInitials()}
                </Avatar>
                {!collapsed && (
                  <div className="crm-profile-info">
                    <div className="crm-profile-name">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="crm-profile-role">
                      {roleAlias(user?.role)}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown>

            {/* ▼▼ aggiornato: companyName piatto dal DTO */}
            {!collapsed && user?.companyName && (
              <div className="crm-company-section">
                <Text className="crm-company-name">{user.companyName}</Text>
              </div>
            )}
          </div>
        </div>
      </Sider>

      <Layout className="crm-main-layout">
        <Content
          className={`crm-content${isSettings ? ' crm-content--settings' : ''}`}
          style={isSettings ? { padding: 0, margin: 0 } : undefined}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default CRMLayout;
